import {HTMLAttributes, useCallback, useState} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {INIPlayer} from '../../../../../../common/shared/ini-player/INIPlayer.interface';
import {IniError} from '../../../../../../common/shared/ini-player/IniError.interface';
import {IniVideoPlayer} from '../../../../../../common/shared/ini-player/IniVideoPlayer';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {DrmInfo, Maybe} from '../../../../../../graphql/generated/types';
import {VideoStatus} from '../constant/videoViewerConstant';
import {useViewerMenuHandler} from '../hook/useViewerMenuHandler';
import {
  resetVideoState,
  setIniError,
  setIsFullscreenEnabled,
  setShowControlBox,
  setVideoState,
} from '../state/videoViewerSlice';
import {storeVolumeToSessionStorage} from '../util/videoPlayerSessionStorage';

export interface IniVideoPlayerContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  hasNextEpisode?: boolean;
  isShortsDrama?: boolean;
  videoRef: React.RefObject<INIPlayer>;
  timeUpdateHandlerEnabled: boolean;
  drmInfo?: Maybe<DrmInfo>;
  videoMetadata: {
    videoWidth?: Maybe<number>;
    videoHeight?: Maybe<number>;
    duration: number;
    url: string;
  };
  startTime?: number;
  setCurrentTime: React.Dispatch<React.SetStateAction<number>>;
  setLoadedProgress: React.Dispatch<React.SetStateAction<number>>;
}

export function IniVideoPlayerContainer({
  hasNextEpisode,
  isShortsDrama,
  videoRef,
  timeUpdateHandlerEnabled,
  videoMetadata,
  drmInfo,
  startTime,
  setCurrentTime,
  setLoadedProgress,
}: IniVideoPlayerContainerProps) {
  const {isMobile} = requestContext.requestInfo.display;
  const {shouldUseNonstopWatching, handleMoveNextProduct} =
    useViewerMenuHandler({
      isMobile,
    });
  const {
    videoState: {videoStatus, isFullScreen, isMute, volume},
  } = useTypedSelector(state => state.videoViewer);
  const dispatch = useTypedDispatch();
  const [isReady, setIsReady] = useState<boolean>(false);

  const onFullScreenChange = useCallback(() => {
    if (!videoRef.current) {
      return;
    }
    const isFullScreen = videoRef.current.isFullScreen();
    // 풀스크린 모드에서는 커스텀 컨트롤이 아니라 네이티브 컨트롤을 사용한다.
    dispatch(
      setVideoState({
        isFullScreen,
      })
    );
  }, [dispatch, videoRef]);

  const onReady = useCallback(() => {
    if (videoRef.current) {
      if (isShortsDrama && !isMute && !isMobile) {
        dispatch(setShowControlBox(true));
      }

      setIsReady(true);
      dispatch(
        setIsFullscreenEnabled(!!videoRef.current?.getIsFullScreenAvailable())
      );
    }
  }, [dispatch, videoRef, isShortsDrama, isMute, isMobile]);

  const onPlay = useCallback(() => {
    const video = videoRef.current;
    // 영상이 끝났는데도 onPlay가 불리는 safari 이슈로 추가되었습니다.(KW-7024)
    if (!video || !isReady || videoStatus === VideoStatus.Ended) {
      return;
    }
    dispatch(setVideoState({videoStatus: VideoStatus.Playing}));
  }, [dispatch, isReady, videoRef, videoStatus]);

  const onPlaying = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isReady) {
      return;
    }
    dispatch(setVideoState({videoStatus: VideoStatus.Playing}));
  }, [dispatch, isReady, videoRef]);

  const onPause = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isReady || videoStatus === VideoStatus.Ended) {
      return;
    }
    dispatch(setVideoState({videoStatus: VideoStatus.Paused}));
  }, [dispatch, isReady, videoRef, videoStatus]);

  const onEnded = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isReady) {
      return;
    }
    video.closeFullScreen();
    dispatch(
      setVideoState({
        videoStatus: VideoStatus.Ended,
      })
    );
    (!isShortsDrama || (isShortsDrama && !hasNextEpisode)) &&
      dispatch(setShowControlBox(true));
    setCurrentTime(videoMetadata.duration);

    if (shouldUseNonstopWatching || isShortsDrama) {
      handleMoveNextProduct();
    }
  }, [
    dispatch,
    handleMoveNextProduct,
    isReady,
    setCurrentTime,
    shouldUseNonstopWatching,
    videoMetadata.duration,
    videoRef,
    isShortsDrama,
    hasNextEpisode,
  ]);

  const onTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || !timeUpdateHandlerEnabled) {
      return;
    }

    const getNextVideoStatus = (prevVideoStatus: VideoStatus) => {
      let nextVideoStatus = undefined;
      // 이미 영상이 끝났는데 끝나기 이전 시간으로 되돌리는 경우 동영상 종료상태가 아니게 되므로 재생상태를 일시정지로 변경해줌
      if (
        prevVideoStatus === VideoStatus.Ended &&
        // API duration과 실제 duration 차이때문에 정상적인 동작을 하지 않아 video.duration으로 처리해둠
        video.currentTime < video.duration
      ) {
        nextVideoStatus = VideoStatus.Paused;
      }
      // 일시정지한 상태에서 영상의 마지막으로 시간이 업데이트 된 경우 동영상이 종료되었으므로 재생상태를 종료로 변경해줌
      else if (
        prevVideoStatus === VideoStatus.Paused &&
        // API duration과 실제 duration 차이때문에 정상적인 동작을 하지 않아 video.duration으로 처리해둠
        video.currentTime >= video.duration
      ) {
        nextVideoStatus = VideoStatus.Ended;
      }
      // 영상이 paused도 아니고 endend도 아니라면 비디오는 play되고 있는 중입니다.
      else if (!video.paused && !video.ended) {
        nextVideoStatus = VideoStatus.Playing;
      }

      return nextVideoStatus;
    };

    const nextVideoStatus = getNextVideoStatus(videoStatus);
    if (nextVideoStatus !== undefined) {
      dispatch(
        setVideoState({
          videoStatus: nextVideoStatus,
        })
      );
    }
    setCurrentTime(video.currentTime);
  }, [
    videoRef,
    timeUpdateHandlerEnabled,
    dispatch,
    videoStatus,
    setCurrentTime,
  ]);

  const onLoading = useCallback(
    (isLoading: boolean) => {
      dispatch(setVideoState({isLoading}));
    },
    [dispatch]
  );

  const onProgress = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    /**
     * 로딩 Progress를 계산합니다.
     * 출처: https://stackoverflow.com/questions/5029519/html5-video-percentage-loaded
     */

    const bf = video.buffered;
    const idx = bf.length - 1 < 0 ? 0 : bf.length - 1;
    if (bf.length > 0) {
      const loaded = bf.end(idx) / video.duration;
      setLoadedProgress(loaded);
    }
  }, [setLoadedProgress, videoRef]);

  const onIniError = useCallback(
    (error: IniError) => {
      dispatch(setIniError(error));
      dispatch(resetVideoState());
    },
    [dispatch]
  );

  const onVolumeChange = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      dispatch(
        setVideoState({
          isMute: video.muted,
          volume: video.volume,
        })
      );
      storeVolumeToSessionStorage(video.volume);
    }
  }, [dispatch, videoRef]);

  return (
    <IniVideoPlayer
      ref={videoRef}
      url={videoMetadata.url}
      width="100%"
      height="auto"
      controls={isFullScreen}
      // 모바일은 volume 값이 변경될 일이 없음
      volume={isShortsDrama || isMobile ? 1 : volume}
      muted={isMute}
      playsinline={true}
      // 음소거 OFF 상태에서는 자동재생을 하지 않습니다.
      autoplay={isMute}
      startTime={startTime}
      loadOnInitialPlay={false}
      useDrm={!!drmInfo}
      drmConfig={drmInfo}
      onReady={onReady}
      onPlay={onPlay}
      onPlaying={onPlaying}
      onPause={onPause}
      onTimeUpdate={onTimeUpdate}
      onEnded={onEnded}
      onLoading={onLoading}
      onFullScreenChange={onFullScreenChange}
      onProgress={onProgress}
      onVolumeChange={onVolumeChange}
      onError={onIniError}
    />
  );
}
